import { useState, useEffect, useCallback } from "react";
import {
  getZephrUserProfile,
  updateZephrUserProfile,
} from "lib/fetchers/zephr/customFields";
import { useUserData } from "lib/store/user";
import { useMixpanelTracking } from "lib/hooks/useMixpanelTracking";
import type { TalkData } from "content-ui/talks/types";
import { datadogLogger } from "lib/datadog/logger";
import { ZephrUserProfileUpdate } from "lib/zephr/profile-types";
import {
  submitTalkRegistration,
  submitTalkJoin,
} from "content-ui/talks/components/registration/registerButtonUtils";
import { parseDelimitedIds, stringifyDelimitedIds } from "lib/utils";

const TALKS_RSVP_FIELD = "talks_rsvp";
const TALKS_CHECKIN_FIELD = "talkcheckin";

export const useTalksZephrRsvp = (_allTalks: TalkData[]) => {
  const { userProfile, isAuthorisedUser, userId } = useUserData();
  const { track } = useMixpanelTracking();

  // Initialize from userProfile.details which already contains talks_rsvp
  const initialRsvps = parseDelimitedIds(userProfile.details.talks_rsvp);
  const [rsvpedTalks, setRsvpedTalks] = useState<string[]>(initialRsvps);
  const isReady = true;

  useEffect(() => {
    // Update state if userProfile.details changes
    const currentRsvps = parseDelimitedIds(userProfile.details.talks_rsvp);
    setRsvpedTalks(currentRsvps);
  }, [userProfile.details.talks_rsvp]);

  const hasRsvped = useCallback(
    (talkId: string) => {
      return rsvpedTalks.includes(talkId);
    },
    [rsvpedTalks]
  );

  const registerRsvp = useCallback(
    async (
      talk: TalkData,
      marketingConsent: boolean | null,
      optionalConsent: boolean | null
    ): Promise<boolean> => {
      if (!isAuthorisedUser) return false;

      try {
        // Fetch FRESH data from Zephr to avoid overwriting existing values
        const userProfile = await getZephrUserProfile();
        const currentRsvpValue = userProfile?.[TALKS_RSVP_FIELD];
        const currentRsvpIds = parseDelimitedIds(currentRsvpValue);

        if (currentRsvpIds.includes(talk.id)) {
          return true; // Already registered
        }

        submitTalkRegistration({
          talk,
          marketingConsent,
          optionalConsent,
          userId,
        });

        const newRsvpIds = Array.from(new Set([...currentRsvpIds, talk.id]));

        const updatePayload: ZephrUserProfileUpdate = {
          [TALKS_RSVP_FIELD]: stringifyDelimitedIds(newRsvpIds),
        };

        const result = await updateZephrUserProfile(updatePayload);

        if (!result.success) {
          throw new Error("Failed to update Zephr profile with new RSVP.");
        }

        setRsvpedTalks(newRsvpIds);
        track("talkRsvp", {
          "Talk ID": talk.id,
          "Talk Name": talk.title,
          "Marketing Consent": marketingConsent,
          "Optional Consent": optionalConsent,
        });

        return true;
      } catch (error) {
        datadogLogger.error(
          "Error registering for talk in Zephr",
          { talkId: talk.id },
          error
        );
        return false;
      }
    },
    [isAuthorisedUser, track, userId]
  );

  const recordTalkCheckIn = useCallback(
    async (talk: TalkData): Promise<boolean> => {
      if (!isAuthorisedUser) return false;

      try {
        // Fetch FRESH data from Zephr to avoid overwriting existing values
        const userProfile = await getZephrUserProfile();
        const currentCheckInValue = userProfile?.[TALKS_CHECKIN_FIELD];
        const currentCheckIns = parseDelimitedIds(currentCheckInValue);

        submitTalkJoin({ talk, userId });

        if (currentCheckIns.includes(talk.id)) {
          return true;
        }

        const newCheckIns = Array.from(new Set([...currentCheckIns, talk.id]));

        const updatePayload: ZephrUserProfileUpdate = {
          [TALKS_CHECKIN_FIELD]: stringifyDelimitedIds(newCheckIns),
        };

        const result = await updateZephrUserProfile(updatePayload);

        if (!result.success) {
          throw new Error("Failed to update Zephr profile with talk check-in.");
        }

        setRsvpedTalks((prev) =>
          prev.includes(talk.id) ? prev : [...prev, talk.id]
        );
        track("talkCheckIn", {
          "Talk ID": talk.id,
          "Talk Name": talk.title,
        });

        return true;
      } catch (error) {
        datadogLogger.error(
          "Error recording talk check-in",
          { talkId: talk.id },
          error
        );
        return false;
      }
    },
    [isAuthorisedUser, track, userId]
  );

  return { isReady, rsvpedTalks, hasRsvped, registerRsvp, recordTalkCheckIn };
};
